/**
 * Normalizers for the Partner API v4 error envelopes (Email Center et al.).
 *
 * Shapes observed against the live API (they differ from the nominal docs):
 *  1. Controller-inline errors (e.g. Twig validation):
 *     { data: null, success: false, message, errors: [{ type, message, severity }] }
 *     — `errors` is an ARRAY without field keys.
 *  2. FormRequest validation via the global exception handler:
 *     { error: { type: 'BadRequestException', code: 422, message: 'The name field is required., …' } }
 *     — one concatenated string, no field keys.
 *  3. abort_if / policies: { error: { type, code, message } }.
 *  4. The nominal Laravel field-keyed shape { errors: { field: [msgs] } } is kept
 *     supported in case other deployments/handlers emit it.
 */
import { getAPIErrorMessage } from '@/utils/request';
import type { FormInstance } from 'antd';

export interface ApiErrorItem {
  message: string;
  type?: string;
  severity?: string;
  line?: number;
}

export interface NormalizedApiError {
  /** Headline, human-readable message. */
  message: string;
  /** Field-keyed messages (Laravel dot-keys) — empty for the array/string envelopes. */
  fieldErrors: Record<string, string[]>;
  /** Itemized error list (e.g. Twig syntax errors) — empty when not itemized. */
  errorList: ApiErrorItem[];
}

const LINE_PATTERN = /\bline\s+(\d+)\b/i;

function toErrorItem(entry: unknown): ApiErrorItem | null {
  if (typeof entry === 'string') {
    const line = entry.match(LINE_PATTERN);
    return { message: entry, line: line ? parseInt(line[1], 10) : undefined };
  }
  if (entry && typeof entry === 'object') {
    const record = entry as Record<string, unknown>;
    const message = typeof record.message === 'string' ? record.message : null;
    if (!message) return null;
    const explicitLine =
      typeof record.line === 'number' ? record.line : undefined;
    const parsedLine = message.match(LINE_PATTERN);
    return {
      message,
      type: typeof record.type === 'string' ? record.type : undefined,
      severity:
        typeof record.severity === 'string' ? record.severity : undefined,
      line:
        explicitLine ?? (parsedLine ? parseInt(parsedLine[1], 10) : undefined),
    };
  }
  return null;
}

export function normalizeApiError(
  data: unknown,
  fallback?: string,
): NormalizedApiError {
  const record = (data && typeof data === 'object' ? data : {}) as Record<
    string,
    unknown
  >;
  const normalized: NormalizedApiError = {
    message: getAPIErrorMessage(record, fallback),
    fieldErrors: {},
    errorList: [],
  };

  const errors = record.errors;
  if (Array.isArray(errors)) {
    normalized.errorList = errors
      .map(toErrorItem)
      .filter((item): item is ApiErrorItem => item !== null);
  } else if (errors && typeof errors === 'object') {
    for (const [key, value] of Object.entries(
      errors as Record<string, unknown>,
    )) {
      if (Array.isArray(value)) {
        normalized.fieldErrors[key] = value.filter(
          (v): v is string => typeof v === 'string',
        );
      } else if (typeof value === 'string') {
        normalized.fieldErrors[key] = [value];
      }
    }
  }

  return normalized;
}

type NamePath = (string | number)[];

/** Laravel dot-key → antd name path: 'sections.0.wait_in_days' → ['sections', 0, 'wait_in_days'] */
export function dotKeyToNamePath(key: string): NamePath {
  return key
    .split('.')
    .map((segment) =>
      /^\d+$/.test(segment) ? parseInt(segment, 10) : segment,
    );
}

interface ApplyServerErrorsOptions {
  /** Remap server keys to form name paths (e.g. { translation_group: ['group'] }). */
  fieldMap?: Record<string, NamePath>;
  /** Receives keys that don't exist in the form (surface them in an Alert). */
  onUnmapped?: (key: string, messages: string[]) => void;
}

/**
 * Push field-keyed server errors onto an antd form. Only keys the form knows
 * are applied; the rest go to onUnmapped. Scrolls to the first applied field.
 */
export function applyServerErrorsToForm(
  form: FormInstance,
  fieldErrors: Record<string, string[]>,
  options: ApplyServerErrorsOptions = {},
): void {
  const { fieldMap = {}, onUnmapped } = options;
  const fields: { name: NamePath; errors: string[] }[] = [];

  for (const [key, messages] of Object.entries(fieldErrors)) {
    const name = fieldMap[key] ?? dotKeyToNamePath(key);
    // getFieldInstance returns undefined for names no Form.Item registered —
    // explicitly mapped keys are trusted, everything else is probed.
    const isRegistered =
      Boolean(fieldMap[key]) ||
      (typeof form.getFieldInstance === 'function'
        ? form.getFieldInstance(name) != null
        : true);
    if (isRegistered) {
      fields.push({ name, errors: messages });
    } else {
      onUnmapped?.(key, messages);
    }
  }

  if (fields.length > 0) {
    form.setFields(fields);
    form.scrollToField?.(fields[0].name, {
      behavior: 'smooth',
      block: 'center',
    });
  }
}

/**
 * Errors destined for the body editor's error list. Sources, in order:
 * itemized errorList entries (the live Twig shape), and field-keyed entries
 * under `body` / `body.<line>` (the nominal documented shape).
 */
export function extractTwigLineErrors(
  normalized: NormalizedApiError,
): { line?: number; message: string }[] {
  const fromList = normalized.errorList.map(({ line, message }) => ({
    line,
    message,
  }));

  const fromFields = Object.entries(normalized.fieldErrors)
    .filter(([key]) => key === 'body' || key.startsWith('body.'))
    .flatMap(([key, messages]) => {
      const lineSegment = key.split('.')[1];
      const line =
        lineSegment && /^\d+$/.test(lineSegment)
          ? parseInt(lineSegment, 10)
          : undefined;
      return messages.map((message) => ({ line, message }));
    });

  return [...fromList, ...fromFields];
}

export type TestSendOutcome = 'success' | 'maybe-sent' | 'error';

/**
 * Known backend bug: the test-send endpoint 500s AFTER the mail has been
 * handed to the mailer, so a 500 means "probably sent, unconfirmed" — not a
 * definite failure. Genuine validation problems still arrive as 4xx.
 */
export function interpretTestSendResult(
  response: { status?: number } | undefined,
): TestSendOutcome {
  const status = response?.status ?? 0;
  if (status >= 200 && status < 300) return 'success';
  if (status === 500) return 'maybe-sent';
  return 'error';
}
